import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button, CircularProgress, Grid, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@material-ui/core';
import { alpha, useTheme } from '@material-ui/core/styles';
import {
  BarChartRounded, DateRangeOutlined, ExpandMoreRounded, InfoOutlined, PeopleOutline,
  PersonAddOutlined, RefreshRounded, TableChartOutlined, TimelineRounded,
} from '@material-ui/icons';
import {
  Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import axios from 'services/auth/jwt/config';
import { getMyAccess } from 'services/adminAccess';
import useStyles from './dashboardStatistics.style';

const number = value => new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 }).format(value);
const Metric = ({ title, value, detail, icon, color }) => {
  const classes = useStyles();
  return (
    <Grid item xs={12} md={4}>
      <CmtCard className={`${classes.card} ${classes.metricCard}`} component="article" aria-label={title}>
        <CmtCardContent className={classes.metricContent}>
          <div className={classes.metricHeader}>
            <Typography component="h2" className={classes.metricTitle}>{title}</Typography>
            <div className={classes.metricIcon} style={{ color, backgroundColor: alpha(color, 0.09) }}>{icon}</div>
          </div>
          <Typography className={classes.metric} style={{ color }}><span dir="ltr">{value}</span></Typography>
          <Typography className={classes.metricDetail}>{detail}</Typography>
        </CmtCardContent>
        <span className={classes.metricAccent} style={{ backgroundColor: alpha(color, 0.55) }} />
      </CmtCard>
    </Grid>
  );
};

const ChartCard = ({ title, subtitle, action, children }) => {
  const classes = useStyles();
  return <CmtCard className={classes.card}>
    <CmtCardHeader
      className={classes.cardHeader} title={title} subTitle={subtitle}
      titleProps={{ component: 'h2', className: classes.cardTitle }}
      subTitleProps={{ className: classes.cardSubtitle }}
    >{action}</CmtCardHeader>
    <CmtCardContent className={classes.chartContent}>{children}</CmtCardContent>
  </CmtCard>;
};

const EmptyState = ({ children }) => {
  const classes = useStyles();
  return <div className={classes.emptyState}><BarChartRounded /><Typography>{children}</Typography></div>;
};

const CountsChart = ({ rows, daily = false, selectedMonth, onSelectMonth }) => {
  const classes = useStyles();
  const theme = useTheme();
  const color = daily ? theme.palette.secondary.main : theme.palette.primary.main;
  return (
    <div className={classes.chart} role="img" aria-label={daily ? 'نمودار ثبت‌نام روزانه' : 'نمودار ثبت‌نام ماهانه'}>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={rows} margin={{ top: 16, right: 12, left: 0, bottom: daily ? 4 : 14 }}>
          <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 5" vertical={false} />
          <XAxis
            dataKey={daily ? 'day' : 'label'} axisLine={false} tickLine={false}
            tickFormatter={daily ? number : undefined} interval="preserveStartEnd"
            angle={daily ? 0 : -25} textAnchor={daily ? 'middle' : 'end'} height={daily ? 30 : 64}
            minTickGap={18} tickMargin={12}
          />
          <YAxis allowDecimals={false} tickFormatter={number} axisLine={false} tickLine={false} width={54} />
          <Tooltip cursor={{ fill: alpha(color, 0.035), radius: 6 }} content={({ active, payload }) => {
            if (!active || !payload || !payload.length) return null;
            const row = payload[0].payload;
            return <div className={classes.tooltip}>
              <Typography variant="body2">{row.label}{row.isCurrent ? ' (تا امروز)' : ''}</Typography>
              <Typography className={classes.tooltipValue}>{number(row.count)} کاربر جدید</Typography>
            </div>;
          }} />
          <Bar
            dataKey="count" fill={alpha(color, 0.8)} radius={[5, 5, 0, 0]} maxBarSize={28}
            cursor={daily ? 'default' : 'pointer'}
            onClick={daily ? undefined : row => onSelectMonth(row.key)}
          >
            {!daily && rows.map(row => <Cell key={row.key} fill={row.key === selectedMonth ? color : alpha(color, 0.24)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const CumulativeChart = ({ rows }) => {
  const classes = useStyles();
  const theme = useTheme();
  const color = theme.palette.primary.main;
  return (
    <div className={classes.chart} role="img" aria-label="نمودار مجموع کاربران از اولین ثبت‌نام">
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={rows} margin={{ top: 12, right: 20, left: 0, bottom: 12 }}>
          <defs><linearGradient id="user-statistics-growth-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.2} />
            <stop offset="100%" stopColor={color} stopOpacity={0.01} />
          </linearGradient></defs>
          <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 5" vertical={false} />
          <XAxis
            dataKey="label" axisLine={false} tickLine={false} interval="preserveStartEnd"
            angle={-25} textAnchor="end" height={64} minTickGap={32} tickMargin={12}
          />
          <YAxis domain={[0, 'auto']} allowDecimals={false} tickFormatter={number} axisLine={false} tickLine={false} width={64} />
          <Tooltip cursor={{ stroke: alpha(color, 0.35), strokeDasharray: '4 4' }} content={({ active, payload }) => {
            if (!active || !payload || !payload.length) return null;
            const row = payload[0].payload;
            return <div className={classes.tooltip}>
              <Typography variant="body2">{row.label}{row.isCurrent ? ' (تا امروز)' : ''}</Typography>
              <Typography className={classes.tooltipValue}>مجموع کاربران: {number(row.total)}</Typography>
              <Typography variant="body2">کاربران جدید این ماه: {number(row.count)}</Typography>
            </div>;
          }} />
          <Area type="monotone" dataKey="total" stroke="none" fill="url(#user-statistics-growth-fill)" isAnimationActive={false} tooltipType="none" />
          <Line
            type="monotone" dataKey="total" stroke={color} strokeWidth={3} isAnimationActive={false}
            dot={rows.length === 1 ? { r: 4 } : false} activeDot={{ r: 6, stroke: theme.palette.background.paper, strokeWidth: 3 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const CountsTable = ({ rows, daily = false, cumulative = false }) => {
  const classes = useStyles();
  const period = cumulative ? 'تجمعی' : daily ? 'روزانه' : 'ماهانه';
  return (
    <details className={classes.tableDetails}>
      <summary className={classes.summary}>
        <TableChartOutlined fontSize="small" /><span>نمایش جدول آمار {period}</span><ExpandMoreRounded className={classes.expandIcon} />
      </summary>
      <div className={classes.tableScroll}>
        <Table size="small" stickyHeader aria-label={`آمار ${period}`}>
          <TableHead><TableRow><TableCell>{daily ? 'روز' : 'ماه'}</TableCell><TableCell align="right">{cumulative ? 'مجموع کاربران' : 'کاربران جدید'}</TableCell></TableRow></TableHead>
          <TableBody>{rows.map(row => <TableRow key={row.key || row.date}>
            <TableCell>{row.label}{row.isCurrent ? ' (تا امروز)' : ''}</TableCell>
            <TableCell align="right">{number(cumulative ? row.total : row.count)}</TableCell>
          </TableRow>)}</TableBody>
        </Table>
      </div>
    </details>
  );
};

const DashboardCharts = () => {
  const classes = useStyles();
  const theme = useTheme();
  const token = useSelector(({ auth }) => auth.token);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', data: null, token: null });
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', data: null, token });
    if (!token) return () => { cancelled = true; };
    const load = async () => {
      try {
        if (!await getMyAccess('UserManager')) {
          if (!cancelled) setState({ status: 'denied', data: null, token });
          return;
        }
        if (cancelled) return;
        const { data } = await axios.get('/user/statistics');
        if (!cancelled) {
          setSelectedMonth(data.currentMonthKey);
          setState({ status: 'ready', data, token });
        }
      } catch (error) {
        if (!cancelled) setState({ status: 'error', data: null, token });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token, attempt]);

  if (state.status === 'loading' || state.token !== token) return (
    <CmtCard className={classes.card}><div className={classes.status} role="status">
      <CircularProgress size={28} thickness={3} /><Typography color="textSecondary">در حال دریافت آمار کاربران…</Typography>
    </div></CmtCard>
  );
  if (state.status === 'denied') return (
    <CmtCard className={classes.card}><div className={classes.status}>
      <PeopleOutline color="disabled" fontSize="large" /><Typography>برای مشاهده آمار کاربران، دسترسی مدیریت کاربران لازم است.</Typography>
    </div></CmtCard>
  );
  if (state.status === 'error') return (
    <CmtCard className={classes.card}><div className={classes.status}>
      <InfoOutlined color="error" fontSize="large" />
      <Typography role="alert">دریافت آمار کاربران ممکن نشد.</Typography>
      <Button variant="outlined" color="primary" startIcon={<RefreshRounded />} onClick={() => setAttempt(value => value + 1)}>تلاش دوباره</Button>
    </div></CmtCard>
  );

  const { data } = state;
  const currentMonth = data.months.find(month => month.key === data.currentMonthKey);
  const month = data.months.find(item => item.key === selectedMonth) || currentMonth;
  const currentYear = data.currentMonthKey.split('-')[0];
  // The 12-month response always includes every elapsed month of the current Jalali year.
  const newUsersThisYear = data.months
    .filter(item => item.key.startsWith(`${currentYear}-`))
    .reduce((total, item) => total + item.count, 0);
  const yearLabel = new Intl.NumberFormat('fa-IR', { useGrouping: false }).format(Number(currentYear));
  const updatedAt = new Intl.DateTimeFormat('fa-IR', {
    timeZone: 'Asia/Tehran', dateStyle: 'medium', timeStyle: 'short',
  }).format(new Date(data.generatedAt));

  return (
    <div>
      <div className={classes.pageHeader}>
        <div className={classes.heading}>
          <div className={classes.headingIcon}><BarChartRounded /></div>
          <div>
            <Typography component="h1" variant="h2">آمار کاربران</Typography>
            <Typography className={classes.subtitle}>آخرین به‌روزرسانی: {updatedAt} (تهران)</Typography>
          </div>
        </div>
        <Button className={classes.refreshButton} variant="outlined" color="primary" startIcon={<RefreshRounded />} onClick={() => setAttempt(value => value + 1)}>به‌روزرسانی آمار</Button>
      </div>
      <Grid container spacing={6}>
        <Metric title="کل کاربران" value={number(data.totalUsers)} detail="تعداد کل کاربران فعال" color={theme.palette.primary.main} icon={<PeopleOutline />} />
        <Metric title="کاربران جدید" value={number(data.newUsersThisMonth)} detail={`${currentMonth.label} — تا این لحظه`} color={theme.palette.secondary.main} icon={<PersonAddOutlined />} />
        <Metric
          title="کاربران جدید سال" value={number(newUsersThisYear)}
          color={theme.palette.success.main} icon={<DateRangeOutlined />}
          detail={`از ۱ فروردین ${yearLabel} تا امروز`}
        />
        <Grid item xs={12}>
          <ChartCard
            title="روند مجموع کاربران از اولین ثبت‌نام"
            subtitle="نمای کلی رشد کاربران، از اولین ثبت‌نام تا امروز"
            action={<span className={classes.badge}><TimelineRounded />تمام دوره</span>}
          >
            {data.cumulativeMonths.length > 0 ? <>
              <div className={classes.chartMeta}>
                <span className={classes.legend}><span className={classes.legendDot} style={{ backgroundColor: theme.palette.primary.main }} />مجموع کاربران تا پایان هر ماه</span>
                <span>{data.cumulativeMonths[0].label} تا {currentMonth.label}</span>
              </div>
              <CumulativeChart rows={data.cumulativeMonths} />
              <Typography className={classes.subtitle}>ماه جاری تا این لحظه محاسبه می‌شود. حساب‌های بدون تاریخ ثبت‌نام در این نمودار محاسبه نمی‌شوند.</Typography>
              <CountsTable rows={data.cumulativeMonths} cumulative />
            </> : <EmptyState>هنوز حسابی با تاریخ ثبت‌نام ثبت نشده است.</EmptyState>}
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="کاربران جدید در ۱۲ ماه اخیر" subtitle="برای نمایش جزئیات روزانه، روی ستون ماه کلیک کنید."
            action={<span className={classes.badge}><DateRangeOutlined />۱۲ ماه اخیر</span>}
          >
            <div className={classes.chartMeta}>
              <span className={classes.legend}><span className={classes.legendDot} style={{ backgroundColor: theme.palette.primary.main }} />کاربران جدید</span>
              <span>ستون پررنگ: ماه انتخاب‌شده</span>
            </div>
            {data.months.every(item => item.count === 0) ? <EmptyState>در این بازه کاربر جدیدی ثبت نشده است.</EmptyState> :
            <CountsChart rows={data.months} selectedMonth={month.key} onSelectMonth={setSelectedMonth} />
            }
            <CountsTable rows={data.months} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title={`جزئیات روزانه ${month.label}`} subtitle="تعداد حساب‌های ایجادشده در هر روز از ماه انتخاب‌شده"
            action={<TextField
                className={classes.monthSelect}
                id="statistics-month" select label="انتخاب ماه" variant="outlined" size="small"
                value={month.key} onChange={event => setSelectedMonth(event.target.value)}
                SelectProps={{ native: true }}
              >
                {data.months.map(item => <option key={item.key} value={item.key}>{item.label}{item.isCurrent ? ' (تا امروز)' : ''}</option>)}
              </TextField>}
          >
            <div className={classes.chartMeta}>
              <span className={classes.legend}><span className={classes.legendDot} style={{ backgroundColor: theme.palette.secondary.main }} />{number(month.count)} کاربر جدید</span>
              <span>{month.isCurrent ? 'تا این لحظه' : 'ماه کامل'}</span>
            </div>
            {month.count === 0 ? <EmptyState>در این ماه کاربر جدیدی ثبت نشده است.</EmptyState> : <CountsChart rows={month.days} daily />}
            <CountsTable rows={month.days} daily />
          </ChartCard>
        </Grid>
      </Grid>
      <div className={classes.footer}><InfoOutlined /><Typography>
        آمار بر اساس زمان ایجاد حساب و ماه‌های شمسی به وقت تهران است؛ حساب‌های ایجادشده توسط مدیر یا ورود گروهی نیز محاسبه می‌شوند.
        حساب‌های مدیریتی و کاربران حذف‌شده در همه آمار کنار گذاشته می‌شوند.
      </Typography></div>
    </div>
  );
};

export default DashboardCharts;
export { Metric, ChartCard, EmptyState };
